import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Save, Plus, Replace, X, AlertTriangle } from "lucide-react";

/**
 * "Save Parameter Values?" 4-option dialog.
 *
 * Triggered when the user saves a Campaign / Scenario / Save-as-Campaign and
 * the parameter source is "manual" or "custom".
 *
 * Props:
 *  open                 – controlled visible state
 *  sourceType           – "manual" | "vendor" | "custom"
 *  selectedVendorName   – name of currently-selected VP (only relevant for "custom")
 *  snapshot             – parameter values currently in the form (used to pre-fill
 *                          New Vendor Parameters mini-form)
 *  canReplace           – whether the user has permission to replace existing VP
 *  onDoNotSave()        – continue saving without touching VP master
 *  onSaveAsNew(name)    – called with user-entered vendor name
 *  onReplaceExisting()  – replace the originally selected VP master
 *  onCancel()           – close without saving anything
 */
export default function SaveParameterDialog({
  open,
  sourceType,
  selectedVendorName,
  snapshot,
  canReplace = true,
  onDoNotSave,
  onSaveAsNew,
  onReplaceExisting,
  onCancel,
}) {
  const [mode, setMode] = useState("choose"); // 'choose' | 'newName' | 'confirmReplace'
  const [name, setName] = useState("");

  const reset = () => { setMode("choose"); setName(""); };

  const close = () => { reset(); onCancel(); };

  const showReplace = sourceType === "custom" && canReplace && !!selectedVendorName;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) close(); }}
    >
      <DialogContent className="max-w-md" data-testid="save-params-dialog">
        <DialogHeader>
          <DialogTitle>Save Parameter Values?</DialogTitle>
        </DialogHeader>

        {mode === "choose" && (
          <>
            <p className="text-sm text-slate-600 leading-relaxed">
              You have entered or modified calculation parameters. Would you like
              to save these values as Vendor Parameters for future use?
            </p>
            <div className="space-y-2 mt-2">
              <OptionButton
                icon={<Save className="h-4 w-4" />}
                title="Do Not Save Vendor Parameters"
                desc="Save the record only. Calculation values are stored inside this record but no master template is created."
                onClick={() => { reset(); onDoNotSave(); }}
                testId="param-opt-skip"
              />
              <OptionButton
                icon={<Plus className="h-4 w-4" />}
                title="Save as New Vendor Parameters"
                desc="Create a new reusable template using the current values."
                onClick={() => setMode("newName")}
                testId="param-opt-new"
              />
              {showReplace && (
                <OptionButton
                  icon={<Replace className="h-4 w-4" />}
                  title="Replace Existing Vendor Parameters"
                  desc={`Update "${selectedVendorName}" master record with the current values.`}
                  onClick={() => setMode("confirmReplace")}
                  testId="param-opt-replace"
                />
              )}
            </div>
            <div className="flex justify-end pt-3 border-t border-slate-100 mt-3">
              <button onClick={close} className="btn-secondary" data-testid="param-opt-cancel">
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          </>
        )}

        {mode === "newName" && (
          <>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Step 2 of 2</div>
            <p className="text-sm text-slate-600">Give the new Vendor Parameters template a name. Calculation values are pre-filled from your current form.</p>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Vendor Parameter Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Combiphar A 25–44"
                data-testid="param-new-name"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
                autoFocus
              />
            </div>
            <SnapshotPreview snapshot={snapshot} />
            <div className="flex justify-between gap-2 pt-3 border-t border-slate-100 mt-3">
              <button onClick={() => setMode("choose")} className="btn-secondary">Back</button>
              <button
                onClick={() => { const n = name.trim(); if (!n) return; reset(); onSaveAsNew(n); }}
                disabled={!name.trim()}
                className="btn-primary"
                data-testid="param-new-submit"
              >
                <Plus className="h-4 w-4" /> Create &amp; Save
              </button>
            </div>
          </>
        )}

        {mode === "confirmReplace" && (
          <>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 leading-relaxed">
                This will update the selected Vendor Parameters master data and may affect future new campaigns or scenarios. <b>Historical saved campaigns/scenarios will not be recalculated.</b>
              </div>
            </div>
            <SnapshotPreview snapshot={snapshot} />
            <div className="flex justify-between gap-2 pt-3 border-t border-slate-100 mt-3">
              <button onClick={() => setMode("choose")} className="btn-secondary">Back</button>
              <button
                onClick={() => { reset(); onReplaceExisting(); }}
                className="btn-primary bg-amber-600 hover:bg-amber-700"
                data-testid="param-replace-confirm"
              >
                <Replace className="h-4 w-4" /> Confirm &amp; Replace
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OptionButton({ icon, title, desc, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-[#0E73C9] hover:bg-[#0E73C9]/4 transition-colors flex items-start gap-3 group"
    >
      <div className="h-8 w-8 rounded-lg bg-slate-100 group-hover:bg-[#0E73C9]/10 flex items-center justify-center shrink-0 text-slate-600 group-hover:text-[#0E73C9]">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </button>
  );
}

function SnapshotPreview({ snapshot }) {
  if (!snapshot) return null;
  const fields = [
    ["Universe", snapshot.universe_size],
    ["TV CPRP", snapshot.tv_cprp ?? snapshot.cpp],
    ["OLV eCPM", snapshot.olv_ecpm ?? snapshot.ecpm],
    ["DDF", snapshot.ddf],
    ["In-Demo", snapshot.in_demo],
    ["Viewability", snapshot.viewability],
  ].filter(([, v]) => v != null && v !== "");
  if (!fields.length) return null;
  return (
    <div className="rounded-lg bg-slate-50 p-3 text-xs space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">Snapshot Preview</div>
      {fields.map(([k, v]) => (
        <div key={k} className="flex justify-between">
          <span className="text-slate-500">{k}</span>
          <span className="font-medium text-slate-800">{typeof v === "number" ? v.toLocaleString("en-US") : v}</span>
        </div>
      ))}
    </div>
  );
}
